import React, { useCallback, useRef, useEffect, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { noop } from 'ui-components/default-props/noop';

import { DisplayGridColumnPropType } from '../../../prop-types/display-grid-column';
import { DisplayGridRecordPropType } from '../../../prop-types/display-grid-record';
import { DisplayGridGroupByRowMetadata } from '../../../prop-types/display-grid-group-record';
import { KeyHandlerService } from '../../../services/key-handler-service';

import { DisplayGridCheckboxCell } from '../display-grid-checkbox-cell';
import { DisplayGridCell } from '../display-grid-cell/index';
import { testId } from '../../../services/testid-service';
import { RowHoverSubject } from '../observables/row-hover-subject';
import { OnRowClickSubject } from '../observables/on-row-click-subject';

import style from './index.module.less';

const leftoverCol = { id: 'leftovers' };

export const DisplayGridBodyRow = React.memo(
  ({
    allData,
    className,
    columns,
    data,
    isExpanded = false,
    isFirstOfType = true,
    isFirstRow = true,
    isSelected = false,
    isDisabled = false,
    onRowDoubleClick,
    onToggleRow,
    pinned = false,
    primaryRow,
    renderCheckbox = true,
    shouldRenderToggler = false,
    isGroupItem = false,
    leftoverColumnWidth = 0,
    totalSelectedGroups = null,
    setTotalSelectedGroups = noop,
  }) => {
    const timeoutId = useRef(null);

    const onCheckboxChange = useCallback((_, event) => {
      event.stopPropagation();
    }, []);

    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
      const subscription = RowHoverSubject.subscribe((rowHoveredId) => setIsHovered(rowHoveredId === data.id));
      return () => subscription.unsubscribe();
    }, [data.id]);

    const onCheckboxClick = useCallback(
      (event) => {
        KeyHandlerService.setControlPressed(event);

        /**
         * The 'shiftKey' property isn't added to React SyntheticEvents, but it is
         * true on the nativeEvent if the user was holding shift, so if that's the
         * case, forward it up to the SyntheticEvent instance to be read upstream.
         */
        const eventWithShiftKey = {
          ...event,
          shiftKey: event.nativeEvent?.shiftKey,
        };
        OnRowClickSubject.next({ event: eventWithShiftKey, rowData: primaryRow });
      },
      [primaryRow],
    );

    /**
     * On row click, set a timeout if this is the first time we've clicked. If we
     * happen to click a second time in quick succession, squash the second click
     * event, as this is actually a double-click event (the browser will fire that
     * one for us), and clear the timeout.
     */
    const onClick = useCallback(
      (event) => {
        if (isDisabled) {
          return;
        }

        if (!onRowDoubleClick) {
          OnRowClickSubject.next({ event, rowData: primaryRow });
          return;
        }

        if (!timeoutId.current) {
          OnRowClickSubject.next({ event, rowData: primaryRow });

          timeoutId.current = global.window.setTimeout(() => {
            timeoutId.current = null;
          }, 300);
        }
      },
      [primaryRow, onRowDoubleClick, isDisabled],
    );

    // If we receive a double-click event, go ahead and clear our click timeout.
    const onDoubleClick = useCallback(
      (event) => {
        if (isDisabled) {
          return;
        }
        if (timeoutId.current) {
          global.window.clearTimeout(timeoutId.current);
          timeoutId.current = null;
        }

        onRowDoubleClick(event, primaryRow);
      },
      [onRowDoubleClick, primaryRow, isDisabled],
    );

    /**
     * We have to use a mouseenter event to alert our currently-hovered row,
     * because of the shape of our DisplayGrid markup, which is in 2 vertical
     * column groups. When we hover a row in one column group, that hovered
     * state should be reflected in the other, so we have to use a javascript
     * alert to keep that state in our reducer to be reflected in the view.
     */
    const onMouseEnter = useCallback((e) => {
      const rowId = get(e, 'currentTarget.dataset.rowId', '');
      RowHoverSubject.next(rowId);
    }, []);

    // If the user clicks a toggle button on an expanded row, trigger this
    // callback, so it can be alerted to any external consumer(s).
    const toggleRow = useCallback(
      () => onToggleRow(primaryRow.groupById || primaryRow.id, !!primaryRow.groupById),
      [primaryRow, onToggleRow],
    );

    const clickTotalSelectGroup = useCallback(
      () =>
        setTotalSelectedGroups((groups) => {
          const newGroups = new Set(groups);
          newGroups.add(data.groupById);
          return newGroups;
        }),
      [setTotalSelectedGroups, data.groupById],
    );

    return (
      <div
        className={classnames(style['grid-rows'], className, {
          [style['group-header']]: data.groupById,
          [style.pinned]: pinned,
          [style['not-pinned']]: !pinned,
          [style['group-item']]: isGroupItem,
          [style['render-shadow']]: pinned && !primaryRow.groupById && columns.length,
          [style.hovered]: !isDisabled && isHovered,
          [style.greyed]: isDisabled,
        })}
        {...testId(`grid-row-${primaryRow.groupById || primaryRow.id}`)}
      >
        {pinned && (
          <DisplayGridCheckboxCell
            checked={isSelected}
            clickTotalSelectGroup={clickTotalSelectGroup}
            data={data}
            dataRowId={primaryRow.groupById || primaryRow.id}
            isExpanded={isExpanded}
            onChange={onCheckboxChange}
            onClick={onCheckboxClick}
            onMouseEnter={onMouseEnter}
            renderCheckbox={renderCheckbox && !isDisabled}
            shouldRenderToggler={shouldRenderToggler}
            toggleRow={toggleRow}
            totalSelectedGroups={totalSelectedGroups}
          />
        )}
        {!data.groupById &&
          columns.map((column) => (
            <DisplayGridCell
              key={column.id}
              allData={allData}
              column={column}
              data={data}
              dataRowId={primaryRow.id}
              isDisabled={isDisabled}
              isExpanded={isExpanded}
              isFirstOfType={isFirstOfType}
              isFirstRow={isFirstRow}
              onClick={onClick}
              onDoubleClick={onDoubleClick}
              onMouseEnter={onMouseEnter}
            />
          ))}
        {!pinned && leftoverColumnWidth > 0 && (
          <DisplayGridCell
            column={leftoverCol}
            dataRowId={primaryRow.groupById || primaryRow.id}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            onMouseEnter={onMouseEnter}
          />
        )}
      </div>
    );
  },
);

DisplayGridBodyRow.propTypes = {
  allData: PropTypes.oneOfType([DisplayGridRecordPropType, DisplayGridGroupByRowMetadata]).isRequired,
  className: PropTypes.string,
  columns: PropTypes.arrayOf(DisplayGridColumnPropType).isRequired,
  data: PropTypes.oneOfType([DisplayGridRecordPropType, DisplayGridGroupByRowMetadata]).isRequired,
  isExpanded: PropTypes.bool,
  isFirstOfType: PropTypes.bool,
  isFirstRow: PropTypes.bool,
  isSelected: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onRowDoubleClick: PropTypes.func.isRequired,
  onToggleRow: PropTypes.func.isRequired,
  pinned: PropTypes.bool,
  primaryRow: PropTypes.oneOfType([DisplayGridRecordPropType, DisplayGridGroupByRowMetadata]).isRequired,
  renderCheckbox: PropTypes.bool,
  shouldRenderToggler: PropTypes.bool,
  isGroupItem: PropTypes.bool,
  totalSelectedGroups: PropTypes.instanceOf(Set),
  leftoverColumnWidth: PropTypes.number,
  setTotalSelectedGroups: PropTypes.func,
};
